import axios, { AxiosRequestConfig, AxiosResponse, AxiosHeaders, AxiosInstance } from 'axios'
import { getUUID } from '@/utils/fingerprint'
import { mathTokens } from './mathToken'
import { REQUEST_NOAUTH_CODE, REQUEST_SUCCESS_CODE } from '../config/constant'
import { $notification, $t } from '@/utils'
import { on, EVENT_TYPES } from '@/utils/eventBus'

import { isTokenExpired, updateLastActiveTime, getUserToken } from '@/utils/user'
import config from '@/config/index'
import i18n from '@/i18n'
import SSEParser from './sseParser'
import TypewriterBuffer, { BufferItems } from './typewriterBuffer'
import store from '@/store'

// 创建打字机实例
// SSEParser
// 定义请求选项接口
export interface RequestOption {
    data?: any
    auth?: boolean
    timeout?: number
    header?: Record<string, any>
    abortController?: AbortController
    onStream?: (chunk: { indexChunk?: number[]; sessionId?: string; sessionMessageId?: string; content?: string }) => void
    onUploadProgress?: (progressEvent: { loaded: number; total: number; percent: number }) => void
}

// 定义响应数据接口
interface ResponseData {
    code: number
    data?: any
    msg?: string
}

// 创建自定义 Axios 实例类型
interface CustomAxiosInstance extends Omit<AxiosInstance, 'get' | 'post'> {
    (config: AxiosRequestConfig): Promise<ResponseData>
    get: (url: string, config?: AxiosRequestConfig) => Promise<ResponseData>
    post: (url: string, data?: any, config?: AxiosRequestConfig) => Promise<ResponseData>
}

// 扩展 AxiosRequestConfig 类型以支持自定义 meta 属性
declare module 'axios' {
    interface AxiosRequestConfig {
        meta?: {
            requestId?: string
            auth?: boolean
        }
    }
}

// 获取当前语言
const getLocale = (): string => {
    return i18n.global.locale.value || 'en'
}

// 获取应用版本
const getAppVersion = (): string => {
    return config.version
}

// 创建 axios 实例
const axiosInstance = axios.create({
    baseURL: '',
    timeout: 60 * 1000,
    headers: {
        'Content-Type': 'application/json'
    },
    // 设置响应拦截器返回 data 而不是完整的 response
    transformResponse: [
        function (data) {
            try {
                return JSON.parse(data)
            } catch (error) {
                return data
            }
        }
    ]
}) as CustomAxiosInstance

// 请求拦截器
axiosInstance.interceptors.request.use(
    async config => {
        // Token 7 天滚动过期检查
        const cachedToken = getUserToken()
        if (cachedToken && isTokenExpired()) {
            store.dispatch('user/clearUserInfo')
            return Promise.reject({ code: REQUEST_NOAUTH_CODE, msg: 'Token expired' })
        }
        if (cachedToken) {
            updateLastActiveTime()
        }

        const timespan = new Date().getTime()
        const deviceCode = await getUUID()
        const locale = getLocale()
        const appVersion = getAppVersion()
        const clientPlatform = 'web'

        // 添加查询参数
        const separator = config.url?.includes('?') ? '&' : '?'
        const urlParams = `lang=${locale}&appVersion=${appVersion}&timespan=${timespan}`

        config.url = config.url + separator + urlParams

        // 生成 token 相关参数
        // 1748491967122 '/api/solve-x-ai/appuser/get-user-info?lang=en&appVersion=2.0.0&timespan=1748491967122'
        const { tokenParam, tokenData, tokenSign, token } = mathTokens(timespan, config.url)

        // 创建新的 headers 对象
        const headers = new AxiosHeaders(config.headers)
        headers.set('Token-Param', tokenParam)
        headers.set('Token-Data', tokenData)
        headers.set('Token-Sign', tokenSign)
        headers.set('Device-Code', deviceCode)
        headers.set('Client-Platform', clientPlatform)

        // 如果需要认证，添加认证头
        if (config.meta?.auth !== false) {
            headers.set('ClientUser-Auth', token ? `crypto ${token}` : '')
        }

        config.headers = headers

        return config
    },
    error => {
        console.error('Request error', error)
        return Promise.reject(error)
    }
)

// 响应拦截器
;(axiosInstance as any).interceptors.response.use(
    (response: AxiosResponse<ResponseData>) => {
        if (response.config.onDownloadProgress) {
            return true
        }

        if (response.status === 200) {
            if (response.data.code === REQUEST_SUCCESS_CODE) {
                response.data.msg = response.data.msg || $t('common.netError')
                return response.data
            } else if (response.data.code === REQUEST_NOAUTH_CODE) {
                store.dispatch('user/clearUserInfo')
                // if (window.location.pathname !== '/') {
                //     window.location.href = '/'
                // }
                response.data.msg = response.data.msg || $t('common.netError')
                return Promise.reject(response.data)
            } else {
                response.data.msg = response.data.msg || $t('common.netError')
                return Promise.reject(response.data)
            }
        } else {
            response.data.msg = response.data.msg || $t('common.netError')
            return Promise.reject(response.data)
        }
    },
    (err: any) => {
        // 如果code是REQUEST_NOAUTH_CODE，则清除用户信息并跳转到首页
        const errorCode = err?.response?.data?.code
        if (errorCode === REQUEST_NOAUTH_CODE) {
            store.dispatch('user/clearUserInfo')
            // if (window.location.pathname !== '/') {
            //     window.location.href = '/'
            // }
        }

        console.error('Request error', err)
        return Promise.reject({ msg: err.message || $t('common.netError') })
    }
)

// 导出常用的请求方法
export const get = (url: string, option: Omit<RequestOption, 'data'> = {}): Promise<ResponseData> => {
    const config: AxiosRequestConfig = {
        url,
        method: 'GET',
        timeout: option.timeout || 60 * 1000,
        headers: option.header || {},
        signal: option.abortController?.signal,
        meta: {
            auth: option.auth !== undefined ? option.auth : true
        }
    }
    return axiosInstance(config)
}

export const post = (url: string, data?: any, option: Omit<RequestOption, 'data'> = {}): Promise<ResponseData> => {
    const config: AxiosRequestConfig = {
        url,
        method: 'POST',
        data,
        timeout: option.timeout || 60 * 1000,
        headers: option.header || {},
        signal: option.abortController?.signal,
        meta: {
            auth: option.auth !== undefined ? option.auth : true
        }
    }

    return axiosInstance(config)
}

export const upload = (url: string, formData: FormData, option: Omit<RequestOption, 'data'> = {}): Promise<ResponseData> => {
    const config: AxiosRequestConfig = {
        url,
        method: 'POST',
        data: formData,
        timeout: option.timeout || 5 * 60 * 1000, // 默认5分钟超时，文件上传需要更长时间
        headers: {
            'Content-Type': 'multipart/form-data',
            ...option.header
        },
        meta: {
            auth: option.auth !== undefined ? option.auth : true
        },
        // 添加上传进度支持
        onUploadProgress: option.onUploadProgress
            ? (progressEvent: any) => {
                  const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                  option.onUploadProgress?.({
                      loaded: progressEvent.loaded,
                      total: progressEvent.total,
                      percent
                  })
              }
            : undefined
    }

    return axiosInstance(config)
}

export const streamPost = (url: string, data?: any, option: Omit<RequestOption, 'data'> = {}): Promise<ResponseData> => {
    const aiTypewriter = new TypewriterBuffer({})
    const sseParser = new SSEParser()
    const config: AxiosRequestConfig = {
        url,
        method: 'POST',
        data,
        timeout: option.timeout || 60 * 1000,
        headers: {},
        meta: {
            auth: option.auth !== undefined ? option.auth : true
        },
        // 添加AbortController支持
        signal: option.abortController?.signal
    }

    // 设置响应类型为流
    config.responseType = 'text'

    return new Promise<ResponseData>((resolve, reject) => {
        let noAuthHandled = false

        aiTypewriter.setDisplayCallback(data => {
            option.onStream?.({
                indexChunk: data?.indexChunk || [],
                content: data?.content || '',
                sessionId: data?.sessionId || '',
                sessionMessageId: data?.sessionMessageId || ''
            })
        })

        aiTypewriter.setEndCallback(() => {
            option.onStream?.({
                content: '[DONE]'
            })
            resolve({ code: REQUEST_SUCCESS_CODE, data: null, msg: '' })

            console.warn('✈️✈️✈️ Writer End', Date.now())
        })

        // 创建SSE解析器实例
        sseParser.reset()

        let lastResponseLength = 0

        // 添加下载进度回调
        config.onDownloadProgress = (progressEvent: any) => {
            if (progressEvent.event.currentTarget?.response || progressEvent.event.target?.response) {
                const fullResponse = progressEvent.event.currentTarget?.response || progressEvent.event.target?.response

                // 非流式场景兜底：如果后端直接返回 JSON 且 code=401，执行与普通请求一致的登出跳转逻辑
                if (!noAuthHandled && typeof fullResponse === 'string') {
                    try {
                        const jsonData = JSON.parse(fullResponse)

                        if (jsonData?.code === REQUEST_NOAUTH_CODE) {
                            noAuthHandled = true
                            store.dispatch('user/logout')
                            option.abortController?.abort()
                            reject(jsonData)
                            return
                        }
                    } catch (e) {
                        if (fullResponse.indexOf('UN_AUTHORIZED') >= 0) {
                            noAuthHandled = true
                            store.dispatch('user/logout')
                            option.abortController?.abort()
                            reject({ code: REQUEST_NOAUTH_CODE })
                            return
                        }
                    }
                }

                // 只处理新增的数据
                const newData = fullResponse.slice(lastResponseLength)

                lastResponseLength = fullResponse.length

                if (!newData) {
                    return
                }

                // 解析新增的数据块
                const newEvents = sseParser.parseChunk(newData)

                if (newEvents.length == 0) {
                    return
                }

                // 检查并处理后端的业务逻辑错误
                const errorEvent = newEvents.find(e => e.command === 'error' || e.code === 500)
                if (errorEvent) {
                    option.abortController?.abort()
                    reject({ code: errorEvent.code, msg: errorEvent.msg || 'AI stream error' })
                    return
                }

                // console.log('newEvents', newEvents)

                // @ts-ignore
                aiTypewriter.addData(newEvents)
            }
        }

        axiosInstance(config)
            .then(() => {
                // 处理流结束时缓冲区可能剩余的最后一条事件
                const finalEvent = sseParser.flushBuffer()
                if (finalEvent) {
                    const errorEvent = finalEvent.command === 'error' || finalEvent.code === 500 ? finalEvent : null
                    if (errorEvent) {
                        reject({ code: errorEvent.code, msg: errorEvent.msg || 'AI stream error' })
                        return
                    }
                    // @ts-ignore
                    aiTypewriter.addData([finalEvent])
                }

                // 确保数据流结束后，打字机能收到结束信号防止UI无限挂起
                // @ts-ignore
                aiTypewriter.addData([{ index: 999999, command: 'done', content: '[DONE]' }])
            })
            .catch(err => {
                reject(err)
            })
    })
        .then(res => {
            return res
        })
        .catch(err => {
            return Promise.reject(err)
        })
}
